import React, { useEffect, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import DynamicDropdown from "../../components/Selector/Selector";
import "./QueueMetrics.css";
import {
  getAverageServicePeriod,
  getAverageWaitingTime,
  getCongestionTimes,
  getDailyReservations,
  getServiceRate
} from "../../services/queue-metrics";
import { getInstitutionBranches } from "../../services/branch";
import { getInstitutionServices } from "../../services/service";
import { getInstitutionEmployee } from "../../services/employee";

const arabicWeekdays = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

function SkeletonCard({ height = 200 }) {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return <div style={{ backgroundColor: '#e5e7eb', borderRadius: '8px', width: '100%', height, animation: 'pulse 2s infinite' }}></div>;
}

function ChartBlock({ title, data, loading, color, bar = false, stacked = false }) {
  return (
    <div className="chart-card">
      <h4 className="chart-title">{title}</h4>
      {loading ? (
        <SkeletonCard height={200} />
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          {stacked ? (
            <BarChart data={data} margin={{ top: 5, right: 10, left: 5, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis
                width={40}
                tick={{
                  fontSize: 12,
                  fill: '#64748b',
                  dx: -8,
                  textAnchor: 'end'
                }}
                axisLine={false}
                tickLine={false}
              />



              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
              <Bar dataKey="completed" stackId="a" fill="#22c55e" radius={[0, 0, 4, 4]} />
              <Bar dataKey={(entry) => entry.reserved - entry.completed} stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : bar ? (
            <BarChart data={data} margin={{ top: 5, right: 10, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis
                width={40}
                tick={{
                  fontSize: 12,
                  fill: '#64748b',
                  dx: -8,
                  textAnchor: 'end'
                }}
                axisLine={false}
                tickLine={false}
              />


              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
              <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis
                width={40}
                tick={{
                  fontSize: 12,
                  fill: '#64748b',
                  dx: -8,
                  textAnchor: 'end'
                }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }} />
              <Line type="monotone" dataKey="value" stroke={color} strokeWidth={3} dot={{ fill: color, strokeWidth: 2, r: 4 }} activeDot={{ r: 6, stroke: color, strokeWidth: 2 }} />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  );
}
const getWeekRange = () => {
  const today = new Date();
  const startOfWeek = new Date(today);
  const day = today.getDay();
  const diff = today.getDate() - day;
  startOfWeek.setDate(diff);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  return {
    start: startOfWeek,
    end: endOfWeek
  };
};
const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const formatArabicDate = (date) => {
  return new Intl.DateTimeFormat("ar-EG", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  }).format(date);
};



export default function QueueMetricsPage() {
  const [viewMode, setViewMode] = useState("weekly");
  const [branchId, setBranchId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(getWeekRange());
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const institution = JSON.parse(localStorage.getItem("institution") || "{}");
  const token = localStorage.getItem("token");
  const institutionId = institution?.id;
  const [metrics, setMetrics] = useState({
    waitData: [],
    serviceData: [],
    reservations: [],
    congestion: [],
    hourlyCongestion: [],
    serviceRate: null,
    avgWait: 0,
    avgWaitSeconds: 0,
    avgService: 0,
    avgServiceSeconds: 0
  });


  const navigateRange = (direction) => {
    const newRange = { ...dateRange };
    const daysToAdd = direction === "next" ? 7 : -7;

    newRange.start = new Date(dateRange.start);
    newRange.start.setDate(newRange.start.getDate() + daysToAdd);

    newRange.end = new Date(dateRange.end);
    newRange.end.setDate(newRange.end.getDate() + daysToAdd);

    setDateRange(newRange);
  };

  const isNextDisabled = () => {
    const today = new Date();
    return dateRange.end >= today;
  };

  const fetchAllMetrics = async () => {
    setLoading(true);

    const rangeStart = dateRange.start.toISOString();
    const rangeEnd = dateRange.end.toISOString();
    const isWeekly = viewMode === "weekly";

    const buildRange = () => {
      const range = [];
      const cursor = new Date(dateRange.start);
      while (cursor <= dateRange.end) {
        range.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
      return range;
    };

    const timeUnits = buildRange();

    try {
      const waitData = await Promise.all(timeUnits.map(async (unit) => {
        const start = new Date(unit);
        start.setHours(0, 0, 0, 0);

        const end = new Date(unit);
        end.setHours(23, 59, 59, 999);

        const res = await getAverageWaitingTime({
          branchId,
          serviceId,
          employeeId,
          startDate: start.toISOString(),
          endDate: end.toISOString()
        });

        const totalSeconds = res?.data?.avgWaitSeconds ?? 0;
        //    console.log(`Average waiting time for ${unit.toDateString()}: ${totalSeconds} seconds`, res.data);
        return {
          name: arabicWeekdays[unit.getDay()],
          value: totalSeconds / 60,
          seconds: totalSeconds
        };

      }));


      const serviceData = await Promise.all(timeUnits.map(async (unit, index) => {
        const start = new Date(unit);
        start.setHours(0, 0, 0, 0);

        const end = new Date(unit);
        end.setHours(23, 59, 59, 999);

        const res = await getAverageServicePeriod({
          branchId,
          serviceId,
          employeeId,
          startDate: start.toISOString(),
          endDate: end.toISOString()
        });

        console.log(`Average service period for ${unit.toDateString()}:`, res.data);

        const seconds = res?.data?.avgServiceSeconds ?? 0;
        const minutes = res?.data?.avgServiceMinutes ?? 0;

        return {
          name: arabicWeekdays[unit.getDay()],
          value: (minutes * 60 + seconds) / 60,
          seconds
        };
      }));

      const reservationsRaw = await getDailyReservations({
        branchId,
        serviceId,
        employeeId,
        startDate: rangeStart,
        endDate: rangeEnd
      });

      const reservationsMap = (reservationsRaw?.data || []).reduce((acc, item) => {
        acc[new Date(item.date).toDateString()] = item;
        return acc;
      }, {});

      const reservations = timeUnits.map(unit => {
        const key = unit.toDateString();
        const day = reservationsMap[key];
        return {
          name: arabicWeekdays[unit.getDay()],
          reserved: day?.reservations ?? 0,
          completed: day?.completed ?? 0
        };
      });

      const congestionRes = await getCongestionTimes({
        branchId,
        serviceId,
        startDate: rangeStart,
        endDate: rangeEnd
      });

      const weeklyDist = congestionRes?.data?.weeklyDistribution || {};
      const hourlyDist = congestionRes?.data?.hourlyDistribution || {};

      const congestion = Object.entries(weeklyDist).map(([day, value]) => ({
        name: {
          Sunday: "الأحد",
          Monday: "الاثنين",
          Tuesday: "الثلاثاء",
          Wednesday: "الأربعاء",
          Thursday: "الخميس",
          Friday: "الجمعة",
          Saturday: "السبت"
        }[day] || day,
        value
      }));

      const hourlyCongestion = Object.entries(hourlyDist).map(([hour, value]) => ({
        name: `${hour}:00`,
        value
      }));

      const serviceRateRes = await getServiceRate({ serviceId, startDate: rangeStart, endDate: rangeEnd });
      const rate = serviceRateRes?.data;

      const totalWaitSeconds = waitData.reduce((sum, item) => sum + item.seconds, 0);
      const totalServiceSeconds = serviceData.reduce((sum, item) => sum + item.seconds, 0);

      setMetrics({
        waitData,
        serviceData,
        reservations,
        congestion,
        hourlyCongestion,
        serviceRate: rate,
        avgWait: Math.floor(totalWaitSeconds / 60),
        avgWaitSeconds: totalWaitSeconds % 60,
        avgService: Math.floor(totalServiceSeconds / 60),
        avgServiceSeconds: totalServiceSeconds % 60
      });
    } catch (err) {
      console.error("Error fetching metrics", err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (branchId || serviceId || employeeId) {
      fetchAllMetrics();
    }
  }, [branchId, serviceId, employeeId, viewMode, dateRange]);

  useEffect(() => {
    if (!institutionId) return;

    getInstitutionServices(institutionId)
      .then(res => setServices(Array.isArray(res?.data) ? res.data : []))
      .catch(console.error);

    getInstitutionBranches(institutionId)
      .then(res => setBranches(Array.isArray(res?.data) ? res.data : []))
      .catch(console.error);

    if (token) {
      getInstitutionEmployee(token)
        .then(res => setEmployees(Array.isArray(res?.data) ? res.data : []))
        .catch(console.error);
    }
  }, [institutionId]);


  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    padding: '1.5rem',
    fontFamily: 'Arial, sans-serif'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '2rem',
    flexWrap: 'wrap',
    gap: '1rem',
    direction: 'rtl'
  };


  const titleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  };

  const iconStyle = {
    width: '48px',
    height: '48px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '20px'
  };

  const mobileFilterToggleStyle = {
    display: window.innerWidth <= 768 ? 'flex' : 'none',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#374151',
    transition: 'all 0.2s ease'
  };

  const controlsContainerStyle = {
    backgroundColor: 'white',
    borderRadius: '16px',
    border: '1px solid #e5e7eb',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    display: showFilters || window.innerWidth > 768 ? 'block' : 'none'
  };

  const controlsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem'
  };

  const dateNavStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    flexWrap: 'nowrap',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };


  const navButtonStyle = {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    minHeight: '32px',
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    padding: 0,
  };

  const dateDisplayStyle = {
  flex: 1,
  textAlign: 'center',
  padding: '8px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '12px',
  fontWeight: '500',
  color: '#111827',
  whiteSpace: 'normal', // ✅ allow wrapping
  wordBreak: 'break-word', // ✅ break long words if needed
  lineHeight: 1.6, // ✅ better vertical spacing
  minHeight: '40px' // ✅ ensure readable space
};



  return (
    <div className="page">
      <div className="content-view">
        <div>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            {/* Header */}
            <div style={headerStyle}>
              <div style={titleContainerStyle}>
                <div style={iconStyle}>📊</div>
                <div>
                  <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: '0', color: '#111827' }}>
                    مؤشرات الطابور
                  </h1>
                  <p style={{ color: '#6b7280', margin: '0.25rem 0 0 0', fontSize: '14px' }}>
                    تتبع أداء الخدمات وأوقات الانتظار
                  </p>
                </div>
              </div>

              <button
                style={mobileFilterToggleStyle}
                onClick={() => setShowFilters(!showFilters)}
              >
                🔍 الفلاتر
              </button>
            </div>

            {/* Controls */}
            <div style={controlsContainerStyle}>
              <div style={controlsGridStyle}>
                <DynamicDropdown
                  label="الفرع"
                  value={branchId}
                  onChange={setBranchId}
                  options={branches.map(b => ({ value: b.id, label: b.name }))}
                />

                <DynamicDropdown
                  label="الخدمة"
                  value={serviceId}
                  onChange={setServiceId}
                  options={services.map(s => ({ value: s.id, label: s.name }))}
                />

                <DynamicDropdown
                  label="الموظف"
                  value={employeeId}
                  onChange={setEmployeeId}
                  options={employees.map(e => ({ value: e.id, label: e.name }))}
                />


              </div>

              {/* Date Navigation */}
              <div style={dateNavStyle}>
                <button
                  style={navButtonStyle}
                  onClick={() => navigateRange("prev")}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                >
                  ←
                </button>

                <div style={dateDisplayStyle}>
                  {`${formatArabicDate(dateRange.start)} - ${formatArabicDate(dateRange.end)}`}
                </div>


                <button
                  style={{
                    ...navButtonStyle,
                    opacity: isNextDisabled() ? 0.5 : 1,
                    cursor: isNextDisabled() ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => navigateRange("next")}
                  disabled={isNextDisabled()}
                  onMouseEnter={(e) => !isNextDisabled() && (e.target.style.backgroundColor = '#e5e7eb')}
                  onMouseLeave={(e) => !isNextDisabled() && (e.target.style.backgroundColor = '#f3f4f6')}
                >
                  →
                </button>
              </div>
            </div>

            {/* Enhanced KPIs */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                border: '1px solid #3b82f6',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ color: '#1d4ed8', fontSize: '14px', marginBottom: '0.5rem' }}>
                  ⏱️ متوسط الخدمة
                </div>
                <div style={{ fontSize: '24px', color: '#1e40af', fontWeight: 'bold' }}>
                  {Math.floor(metrics.avgService)} د {metrics.avgServiceSeconds ? Math.floor(metrics.avgServiceSeconds) + ' ث' : ''}
                </div>

              </div>

              <div style={{
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                border: '1px solid #22c55e',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ color: '#15803d', fontSize: '14px', marginBottom: '0.5rem' }}>
                  ⏳ متوسط الانتظار
                </div>
                <div style={{ fontSize: '24px', color: '#166534', fontWeight: 'bold' }}>
                  {metrics.avgWait} د {metrics.avgWaitSeconds} ث
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #fae8ff 0%, #f3e8ff 100%)',
                border: '1px solid #a855f7',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ color: '#7c3aed', fontSize: '14px', marginBottom: '0.5rem' }}>
                  📈 معدل الإتمام
                </div>
                <div style={{ fontSize: '24px', color: '#6b21a8', fontWeight: 'bold' }}>
                  {metrics.serviceRate?.serviceRate || 0}%
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                border: '1px solid #ef4444',
                borderRadius: '16px',
                padding: '1.5rem',
                textAlign: 'center'
              }}>
                <div style={{ color: '#dc2626', fontSize: '14px', marginBottom: '0.5rem' }}>
                  👥 نسبة غير المخدومين
                </div>
                <div style={{ fontSize: '24px', color: '#b91c1c', fontWeight: 'bold' }}>
                  {metrics.serviceRate?.dropOffRate || 0}%
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="charts-grid">
              <ChartBlock title="🕓 وقت الخدمة" data={metrics.serviceData} loading={loading} color="#6366f1" />
              <ChartBlock title="⏳ وقت الانتظار" data={metrics.waitData} loading={loading} color="#22c55e" />
              <ChartBlock title="📆 عدد الحجوزات" data={metrics.reservations} loading={loading} stacked color="#f97316" />
              <ChartBlock title="🚦 أوقات الذروة (أيام)" data={metrics.congestion} loading={loading} color="#0ea5e9" bar />
              <ChartBlock title="⏰ أوقات الذروة (ساعات)" data={metrics.hourlyCongestion} loading={loading} color="#a855f7" bar />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}